import React, { useState } from 'react';

const CopyButton = ({ text }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = (e) => {
    e.stopPropagation();
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <button className="copy-btn" onClick={handleCopy} title="Copy to clipboard">
      {copied ? '✓' : '⧉'}
    </button>
  );
};

const endpoints = [
  { chain: 'Tezos', network: 'Mainnet', path: '/rpc/tezos/mainnet' },
  { chain: 'Tezos', network: 'Shadownet', path: '/rpc/tezos/shadownet' },
  { chain: 'Etherlink', network: 'Mainnet', path: '/rpc/etherlink/mainnet' },
  { chain: 'Etherlink', network: 'Shadownet', path: '/rpc/etherlink/shadownet' },
];

const RainbowDocs = () => (
  <div className="gateway-docs">
    <p>Content-addressed data via path-style URLs.</p>
    <dl>
      <dt>IPFS</dt>
      <dd><code>https://rainbow.tcinfra.net/ipfs/&lt;cid&gt;[/&lt;path&gt;]</code></dd>
      <dt>IPNS</dt>
      <dd><code>https://rainbow.tcinfra.net/ipns/&lt;name&gt;[/&lt;path&gt;]</code></dd>
      <dt>Example</dt>
      <dd><code>https://rainbow.tcinfra.net/ipfs/bafybeigdyrzt5sfp7udm7hu76uh7y26nf3efuylqabf3oclgtqy55fbzdi</code></dd>
    </dl>
  </div>
);

const EXAMPLE_CID = 'zdj7WgzjPJDZW7szwjzXpD1YZjSmBuvfcQrLS3YTssb1BomU3';
const PRESETS = ['tiny', 'thumb', 'card', 'hero'];
const FORMATS = ['webp', 'avif', 'jpg'];
const EXAMPLE_SIZES = {
  webp: { tiny: '696 B', thumb: '23 KB', card: '113 KB', hero: '368 KB' },
  avif: { tiny: '883 B', thumb: '23 KB', card: '122 KB', hero: '416 KB' },
  jpg:  { tiny: '649 B', thumb: '21 KB', card: '111 KB', hero: '404 KB' },
};

const ImgDocs = () => (
  <div className="gateway-docs">
    <p>
      On-the-fly image transcoding. Fetches <code>&lt;source&gt;</code>, applies{' '}
      <code>&lt;preset&gt;</code>, serves as <code>&lt;format&gt;</code>.
    </p>

    <h4>Sources</h4>
    <dl>
      <dt>IPFS</dt>
      <dd><code>ipfs/&lt;cid&gt;[/&lt;path&gt;]</code></dd>
      <dt>IPNS</dt>
      <dd><code>ipns/&lt;name&gt;[/&lt;path&gt;]</code></dd>
      <dt>Web</dt>
      <dd>
        <code>web/&lt;scheme&gt;/&lt;host&gt;[/&lt;path&gt;][?&lt;query&gt;]</code>
        <span className="docs-note"> — scheme = <code>http</code> | <code>https</code></span>
      </dd>
    </dl>

    <h4>Presets</h4>
    <p className="docs-note">
      Each preset sets a maximum dimension. Aspect ratio is preserved; images are never upscaled.
    </p>
    <table className="docs-table">
      <thead>
        <tr>
          <th>Preset</th>
          <th>Max dimensions</th>
          <th>Quality</th>
          <th>Typical use</th>
        </tr>
      </thead>
      <tbody>
        <tr><td><code>tiny</code></td><td>—</td><td>—</td><td>Inline blur preview (see below)</td></tr>
        <tr><td><code>thumb</code></td><td>300 × 300 (fit)</td><td>80</td><td>Avatars, list rows</td></tr>
        <tr><td><code>card</code></td><td>800 × any (fit width)</td><td>82</td><td>Grid tiles, cards</td></tr>
        <tr><td><code>hero</code></td><td>1600 × any (fit width)</td><td>85</td><td>Page banners</td></tr>
      </tbody>
    </table>
    <p className="docs-note">
      Need a new preset? Ping the maintainer — presets are compiled into the binary, not configurable per request.
    </p>

    <h4>Formats</h4>
    <table className="docs-table">
      <thead>
        <tr>
          <th>Ext</th>
          <th>Content-Type</th>
          <th>Notes</th>
        </tr>
      </thead>
      <tbody>
        <tr><td><code>webp</code></td><td><code>image/webp</code></td><td>Smallest universally-supported modern format. Safe default.</td></tr>
        <tr><td><code>avif</code></td><td><code>image/avif</code></td><td>~20–30% smaller than WebP. Supported by Chrome/Firefox/Safari.</td></tr>
        <tr><td><code>jpg</code></td><td><code>image/jpeg</code></td><td>Universal fallback. Use for <code>&lt;img&gt;</code> without <code>&lt;picture&gt;</code>.</td></tr>
      </tbody>
    </table>

    <div className="examples-block">
      <div className="examples-label">Examples:</div>
      {FORMATS.map((fmt) => (
        <div className="examples-row" key={fmt}>
          <span className="examples-fmt">{fmt}</span>
          {PRESETS.map((p) => (
            <a
              key={p}
              className="example-btn"
              href={`https://img.tcinfra.net/img/${p}.${fmt}/ipfs/${EXAMPLE_CID}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              {p}
              <span className="example-size">{EXAMPLE_SIZES[fmt][p]}</span>
            </a>
          ))}
        </div>
      ))}
      <p className="docs-note tiny-note">
        <code>tiny</code> returns an inlineable blurred placeholder — embed it directly in API responses so clients can render a preview while the full-quality image loads.
      </p>
    </div>
  </div>
);

const gateways = [
  { service: 'IPFS', url: 'https://rainbow.tcinfra.net', details: <RainbowDocs /> },
  { service: 'Images', url: 'https://img.tcinfra.net/img/<preset>.<format>/<source>', details: <ImgDocs /> },
];

const Landing = () => {
  const [expanded, setExpanded] = useState(null);

  return (
    <div className="container">
      <div className="landing-hero">
        <h1>TCInfra</h1>
        <p>RPC Node Provider for Tezos & Etherlink</p>

        <div className="endpoint-table-wrapper">
          <h2 className="section-title">RPC Endpoints</h2>
          <table>
            <thead>
              <tr>
                <th>Chain</th>
                <th>Network</th>
                <th>Endpoint</th>
              </tr>
            </thead>
            <tbody>
              {endpoints.map((ep) => (
                <tr key={ep.path}>
                  <td>{ep.chain}</td>
                  <td>{ep.network}</td>
                  <td className="endpoint-cell">
                    <code>https://tcinfra.net{ep.path}</code>
                    <CopyButton text={`https://tcinfra.net${ep.path}`} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="endpoint-table-wrapper">
          <h2 className="section-title">Gateways</h2>
          <table>
            <thead>
              <tr>
                <th>Service</th>
                <th>Endpoint</th>
              </tr>
            </thead>
            <tbody>
              {gateways.map((gw, i) => {
                const isOpen = expanded === i;
                return (
                  <React.Fragment key={gw.url}>
                    <tr
                      className={`gateway-row ${isOpen ? 'open' : ''}`}
                      onClick={() => setExpanded(isOpen ? null : i)}
                    >
                      <td>
                        <span className="chevron">▸</span>
                        {gw.service}
                      </td>
                      <td className="endpoint-cell">
                        <code>{gw.url}</code>
                        <CopyButton text={gw.url} />
                      </td>
                    </tr>
                    {isOpen && (
                      <tr className="gateway-details-row">
                        <td colSpan={2}>{gw.details}</td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>

        <p className="powered-by">
          Powered by <a href="https://tezoscommons.org" target="_blank" rel="noopener noreferrer">Tezos Commons</a>
        </p>
      </div>
    </div>
  );
};

export default Landing;
